import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Star, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Review {
  id: string;
  order_id: string;
  rating: number;
  review_text: string | null;
  reviewer_name: string | null;
  occasion: string | null;
  is_featured: boolean;
  is_approved: boolean;
  created_at: string;
}

const eventTypeLabels: Record<string, string> = {
  wedding: "Wedding", birthday: "Birthday", anniversary: "Anniversary",
  graduation: "Graduation", memorial: "Memorial", proposal: "Proposal",
  baby_shower: "Baby Shower", retirement: "Retirement", other: "Other",
};

const AdminReviews = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/login");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!isAdmin) return;
      const { data } = await supabase
        .from("reviews")
        .select("*")
        .order("created_at", { ascending: false });
      if (data) setReviews(data as Review[]);
      setLoading(false);
    };
    if (isAdmin) fetchReviews();
  }, [isAdmin]);

  const toggleField = async (id: string, field: "is_featured" | "is_approved", current: boolean) => {
    const { error } = await supabase
      .from("reviews")
      .update({ [field]: !current })
      .eq("id", id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }

    setReviews((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: !current } : r))
    );
    toast({ title: `Review ${!current ? (field === "is_featured" ? "featured" : "approved") : (field === "is_featured" ? "unfeatured" : "unapproved")}` });
  };

  if (authLoading || adminLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button variant="ghost" className="mb-6" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
          </Button>

          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Customer Reviews</h1>
          <p className="text-muted-foreground mb-8">Approve and feature reviews to display them on the homepage.</p>

          {reviews.length === 0 ? (
            <div className="bg-card rounded-2xl shadow-soft p-12 text-center">
              <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No reviews yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review, i) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="bg-card rounded-xl shadow-soft p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      {/* Stars */}
                      <div className="flex gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= review.rating
                                ? "fill-primary text-primary"
                                : "text-muted-foreground/20"
                            }`}
                          />
                        ))}
                      </div>

                      {review.review_text && (
                        <p className="text-foreground text-sm mb-2">"{review.review_text}"</p>
                      )}

                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {review.reviewer_name || "Anonymous"}
                        </span>
                        {review.occasion && (
                          <>
                            <span>·</span>
                            <span>{eventTypeLabels[review.occasion] || review.occasion}</span>
                          </>
                        )}
                        <span>·</span>
                        <span>{new Date(review.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant={review.is_approved ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleField(review.id, "is_approved", review.is_approved)}
                      >
                        {review.is_approved ? (
                          <><CheckCircle className="w-4 h-4 mr-1" /> Approved</>
                        ) : (
                          <><XCircle className="w-4 h-4 mr-1" /> Approve</>
                        )}
                      </Button>
                      <Button
                        variant={review.is_featured ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleField(review.id, "is_featured", review.is_featured)}
                      >
                        {review.is_featured ? (
                          <><Eye className="w-4 h-4 mr-1" /> Featured</>
                        ) : (
                          <><EyeOff className="w-4 h-4 mr-1" /> Feature</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Status badges */}
                  <div className="flex gap-2 mt-3">
                    {review.is_approved && review.is_featured && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        ✨ Showing on homepage
                      </span>
                    )}
                    {review.is_approved && !review.is_featured && (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                        Approved but not featured
                      </span>
                    )}
                    {!review.is_approved && (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                        Pending approval
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminReviews;
